import React, { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchOpenBetsAsync } from "../../store/slices/betSlice";
import { formatDateTimeUtils } from "../../utils/comman";

const MarketAnalysis = () => {
  const dispatch = useAppDispatch();
  const openBets = useAppSelector((state) => state.bet?.openBets);
  const exposure = useAppSelector(
    (state) => state.bet?.balance?.liability ?? 0,
  );

  useEffect(() => {
    dispatch(fetchOpenBetsAsync());
  }, [dispatch]);

  const { rows, meta } = useMemo(() => {
    const empty = { rows: [], meta: null };
    if (!openBets) return empty;

    // Shape A: [{ metadata: [...], data: [...] }]
    if (Array.isArray(openBets)) {
      const first = openBets[0] || {};
      const metaCandidate = Array.isArray(first?.metadata)
        ? (first.metadata?.[0] ?? null)
        : null;

      // openBets[0].data[]
      if (Array.isArray(first?.data) && first.data.length) {
        // If items look like bet rows (has match_id/market_id etc.), keep them.
        const looksLikeBetRow = first.data.some(
          (x) => x && (x.market_id != null || x.match_id != null),
        );
        return {
          rows: looksLikeBetRow ? first.data : [],
          meta: metaCandidate,
        };
      }

      // Sometimes nested one level deeper: openBets[0].data.data[]
      if (Array.isArray(first?.data?.data)) {
        return { rows: first.data.data, meta: metaCandidate };
      }

      return { rows: [], meta: metaCandidate };
    }

    // Shape B: { data: [...] }
    if (Array.isArray(openBets?.data)) {
      const firstGroup = openBets.data[0] || {};
      const metaCandidate = Array.isArray(firstGroup?.metadata)
        ? (firstGroup.metadata?.[0] ?? null)
        : null;

      // If openBets.data is already an array of bet rows
      const looksLikeBetRow = openBets.data.some(
        (x) => x && (x.market_id != null || x.match_id != null),
      );
      if (looksLikeBetRow) {
        return { rows: openBets.data, meta: null };
      }

      // Otherwise assume openBets.data[0].data is the bet rows
      if (Array.isArray(firstGroup?.data)) {
        return { rows: firstGroup.data, meta: metaCandidate };
      }

      return { rows: [], meta: metaCandidate };
    }

    // Shape C: { bets: [...] }
    if (Array.isArray(openBets?.bets)) {
      return { rows: openBets.bets, meta: null };
    }

    return empty;
  }, [openBets]);

  const totals = useMemo(() => {
    const totalCountFromMeta = meta?.total != null ? Number(meta.total) : null;
    const totalProfitFromMeta =
      meta?.total_profit != null ? Number(meta.total_profit) : null;

    const computedProfit = rows.reduce((sum, b) => {
      const v = b?.profit ?? b?.p_l ?? 0;
      const n = Number(v);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);

    return {
      totalCount: totalCountFromMeta ?? rows.length,
      totalProfit: totalProfitFromMeta ?? computedProfit,
    };
  }, [meta, rows]);

  const formatNumber = (v) => {
    if (v == null || v === "") return "-";
    const n = Number(v);
    if (!Number.isFinite(n)) return "-";
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
  };

  return (
    <section className="section accounts">
      <div className="row g-0">
        <div className="col-xl-12">
          <div className="div">
            <h2 className="userscreen-title d-flex justify-content-between">
              Market Analysis{" "}
              <div>
                Total Exposure : <b>{exposure}</b>
              </div>
            </h2>
          </div>

          <div className="openBetsTabs accounts">
            <div className="table-responsive">
              <table className="table table-striped table-bordered">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Sport</th>
                    <th>Series</th>
                    <th>Match</th>
                    <th>Market</th>
                    <th>Selection</th>
                    <th>Side</th>
                    <th>Odds</th>
                    <th>Stake</th>
                    <th>Liability</th>
                    <th>Profit</th>
                    <th>Date/Time</th>
                  </tr>
                </thead>
                <tbody>
                  {!openBets && (
                    <tr>
                      <td colSpan={12} className="text-center">
                        Loading...
                      </td>
                    </tr>
                  )}

                  {openBets && rows.length === 0 && (
                    <tr>
                      <td colSpan={12} className="text-center">
                        No Data Found
                      </td>
                    </tr>
                  )}

                  {rows.map((bet, index) => {
                    const isBack =
                      typeof bet?.is_back === "number"
                        ? bet.is_back === 1
                        : !!bet?.is_back;
                    const sideLabel = isBack ? "Back" : "Lay";

                    const profitVal = bet?.profit ?? bet?.p_l ?? null;
                    const profitNum = Number(profitVal);
                    const profitColor =
                      Number.isFinite(profitNum) && profitNum < 0
                        ? "red"
                        : "green";

                    const stakeVal =
                      bet?.stack ??
                      bet?.size ??
                      bet?.stake ??
                      bet?.amount ??
                      null;

                    return (
                      <tr key={bet?.bet_id || bet?.market_id || index}>
                        <td>{index + 1}</td>
                        <td>{bet?.sport_name ?? "-"}</td>
                        <td>{bet?.series_name ?? "-"}</td>
                        <td>{bet?.match_name ?? "-"}</td>
                        <td>{bet?.market_name ?? "-"}</td>
                        <td>
                          {bet?.selection_name ?? bet?.selectionName ?? "-"}
                        </td>
                        <td>{sideLabel}</td>
                        <td>{formatNumber(bet?.odds ?? bet?.price)}</td>
                        <td>{formatNumber(stakeVal)}</td>
                        <td>{formatNumber(bet?.liability)}</td>
                        <td>{formatNumber(profitVal)}</td>
                        <td>
                          {formatDateTimeUtils(
                            bet?.createdAt ?? bet?.created_at,
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MarketAnalysis;
